import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Upload, X, ImagePlus, Search, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useSearchCards, useAddToCollection, useSets, useImportSetCards } from '@/hooks/useApi';
import { formatPrice } from '@/utils';
import toast from 'react-hot-toast';

const CONDITIONS = ['Mint', 'Near Mint', 'Excellent', 'Good', 'Played', 'Poor'] as const;

const GAMES = ['Pokemon', 'Magic: The Gathering', 'Yu-Gi-Oh!', 'Dragon Ball Super', 'One Piece', 'Flesh and Blood', 'Other'];

export default function AddCardsPage() {
  const navigate = useNavigate();

  // Step
  const [step, setStep] = useState(1);

  // Card selection
  const [entryMode, setEntryMode]     = useState<'search' | 'manual'>('search');
  const [cardSearch, setCardSearch]   = useState('');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [createdCardId, setCreatedCardId]   = useState<string | null>(null);
  const [manualCard, setManualCard] = useState({
    name: '', set_name: '', game: 'Pokemon', rarity: '', card_type: '', card_number: '',
  });

  // Images
  const [imageFiles, setImageFiles]       = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Collection details
  const [condition, setCondition]               = useState<string>('Near Mint');
  const [quantity, setQuantity]                 = useState(1);
  const [acquisitionPrice, setAcquisitionPrice] = useState('');
  const [notes, setNotes]                       = useState('');
  const [isForTrade, setIsForTrade]             = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone]                 = useState(false);

  const [setImporting, setSetImporting] = useState(false);

  const { data: searchResults, isLoading: searching } = useSearchCards(cardSearch);
  const { mutateAsync: addCard } = useAddToCollection();
  const { mutateAsync: importSet } = useImportSetCards();
  const { data: availableSets = [] } = useSets(manualCard.game);

  // When the user picks a set name that matches one from the datalist,
  // auto-trigger an import so cards are ready in the DB.
  const handleSetNameChange = async (value: string) => {
    setManualCard((p) => ({ ...p, set_name: value }));
    const sets = availableSets as string[];
    const matched = sets.find((s) => s.toLowerCase() === value.toLowerCase());
    if (!matched || !manualCard.game) return;
    // Only import if not already in DB (the backend skips existing cards)
    setSetImporting(true);
    try {
      const result = await importSet({ game: manualCard.game, set_name: matched });
      if (result.imported > 0) {
        toast.success(`Loaded ${result.imported} cards from ${matched}`);
      }
    } catch {
      // non-critical — user can still add manually
    } finally {
      setSetImporting(false);
    }
  };

  const searchList: any[] = Array.isArray(searchResults)
    ? searchResults
    : (searchResults as any)?.data ?? [];

  const selectedCard = searchList.find((c) => c.id === selectedCardId) ?? null;
  const effectiveCardId = selectedCardId ?? createdCardId;

  const canProceedStep1 =
    entryMode === 'search'
      ? !!effectiveCardId
      : !!manualCard.name && !!manualCard.game;

  // ── Image handling ──────────────────────────────────────────────────────────

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, 5 - imageFiles.length);
    if (!files.length) return;
    setImageFiles((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = (i: number) => {
    setImageFiles((prev) => prev.filter((_, idx) => idx !== i));
    setImagePreviews((prev) => prev.filter((_, idx) => idx !== i));
  };

  const uploadImages = async (): Promise<string[]> => {
    if (!imageFiles.length) return [];
    const formData = new FormData();
    imageFiles.forEach((file) => formData.append('images', file));
    try {
      const apiVersion = import.meta.env.VITE_API_VERSION || 'v1';
      const res = await fetch(`/api/${apiVersion}/uploads/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        body: formData,
      });
      const data = await res.json();
      return data.data?.urls ?? data.urls ?? [];
    } catch {
      return [];
    }
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setIsSubmitting(true);

    let finalCardId = effectiveCardId;

    // Create card record if entered manually
    if (entryMode === 'manual' && !finalCardId) {
      try {
        const apiVersion = import.meta.env.VITE_API_VERSION || 'v1';
        const res = await fetch(`/api/${apiVersion}/cards`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify({
            name:        manualCard.name,
            set_name:    manualCard.set_name || 'Unknown',
            game:        manualCard.game,
            rarity:      manualCard.rarity      || undefined,
            card_type:   manualCard.card_type   || undefined,
            card_number: manualCard.card_number || undefined,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error?.message ?? 'Failed to create card');
        finalCardId = data.data.id;
        setCreatedCardId(finalCardId);
      } catch (err: any) {
        console.error('Card creation failed:', err);
        setIsSubmitting(false);
        return;
      }
    }

    if (!finalCardId) { setIsSubmitting(false); return; }

    try {
      const uploadedUrls = await uploadImages();
      await addCard({
        card_id:           finalCardId,
        condition,
        quantity,
        acquisition_price: acquisitionPrice ? Number(acquisitionPrice) : null,
        notes:             notes.trim() || null,
        is_for_trade:      isForTrade,
        image_urls:        uploadedUrls,
      } as any);

      setDone(true);
    } catch (err) {
      console.error('Failed to add to collection:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Done screen ─────────────────────────────────────────────────────────────

  if (done) {
    const cardName = selectedCard?.name ?? manualCard.name ?? 'Card';
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-zinc-100 mb-2">Added to Collection!</h2>
        <p className="text-gray-500 dark:text-zinc-500 mb-8">
          {quantity}× <strong>{cardName}</strong> ({condition}) has been added.
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => { setStep(1); setDone(false); setSelectedCardId(null); setCreatedCardId(null); setCardSearch(''); setManualCard({ name: '', set_name: '', game: 'Pokemon', rarity: '', card_type: '', card_number: '' }); setImageFiles([]); setImagePreviews([]); setCondition('Near Mint'); setQuantity(1); setAcquisitionPrice(''); setNotes(''); setIsForTrade(false); }}
            className="px-6 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm font-medium hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
          >
            Add Another Card
          </button>
          <button
            onClick={() => navigate('/collection')}
            className="px-6 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 transition-colors"
          >
            View Collection
          </button>
        </div>
      </div>
    );
  }

  // ── Page layout ─────────────────────────────────────────────────────────────

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-accent-500 text-white py-10">
        <div className="container-custom">
          <button
            onClick={() => navigate('/collection')}
            className="flex items-center gap-1.5 text-brand-100 hover:text-white text-sm mb-3 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Collection
          </button>
          <h1 className="text-3xl font-display font-bold mb-1">Add to Collection</h1>
          <p className="text-brand-100">Search the database or enter card details manually</p>
        </div>
      </div>

      <div className="container-custom py-8 max-w-2xl">
        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step > s  ? 'bg-brand-600 text-white' :
                step === s ? 'bg-brand-600 text-white ring-4 ring-brand-100' :
                'bg-gray-200 dark:bg-zinc-700 text-gray-500 dark:text-zinc-500'
              }`}>
                {step > s ? '✓' : s}
              </div>
              {s < 2 && <ChevronRight className="h-4 w-4 text-gray-400 dark:text-zinc-500" />}
            </div>
          ))}
          <div className="ml-2 flex gap-8 text-sm font-medium">
            <span className={step >= 1 ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-zinc-500'}>Select Card</span>
            <span className={step >= 2 ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-zinc-500'}>Details</span>
          </div>
        </div>

        {/* ── STEP 1: Select Card ─────────────────────────────────────────── */}
        {step === 1 && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6">
            <h2 className="text-xl font-bold mb-5">Which card are you adding?</h2>

            {/* Toggle */}
            <div className="flex gap-2 mb-5">
              {(['search', 'manual'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setEntryMode(mode)}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${
                    entryMode === mode
                      ? 'bg-brand-600 text-white border-brand-600'
                      : 'bg-white dark:bg-zinc-900 text-gray-600 dark:text-zinc-400 border-gray-200 dark:border-zinc-800 hover:border-gray-300 dark:hover:border-zinc-600 dark:border-zinc-700'
                  }`}
                >
                  {mode === 'search' ? 'Search Database' : 'Enter Manually'}
                </button>
              ))}
            </div>

            {/* Search mode */}
            {entryMode === 'search' && (
              <>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search by card name…"
                    value={cardSearch}
                    onChange={(e) => setCardSearch(e.target.value)}
                    className="w-full pl-10 pr-4 border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>

                {searching && <p className="text-sm text-gray-500 dark:text-zinc-500 mb-4">Searching…</p>}

                {searchList.length > 0 && (
                  <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
                    {searchList.map((card: any) => (
                      <button
                        key={card.id}
                        onClick={() => setSelectedCardId(card.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                          selectedCardId === card.id
                            ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/20'
                            : 'border-gray-200 dark:border-zinc-800 hover:border-gray-300 dark:hover:border-zinc-600 dark:border-zinc-700'
                        }`}
                      >
                        {card.image_url ? (
                          <img src={card.image_url} alt={card.name} className="h-12 w-9 object-cover rounded" />
                        ) : (
                          <div className="h-12 w-9 bg-gray-100 dark:bg-zinc-800 rounded flex items-center justify-center text-xl">🃏</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{card.name}</p>
                          <p className="text-xs text-gray-500 dark:text-zinc-500">{card.set_name}{card.rarity ? ` · ${card.rarity}` : ''}</p>
                        </div>
                        {card.market_price != null && (
                          <p className="ml-auto text-brand-600 dark:text-brand-400 font-bold text-sm flex-shrink-0">{formatPrice(card.market_price)}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {!searching && searchList.length === 0 && cardSearch.length > 1 && (
                  <p className="text-sm text-gray-500 dark:text-zinc-500 mb-4">No cards found — try entering manually.</p>
                )}
              </>
            )}

            {/* Manual mode */}
            {entryMode === 'manual' && (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Card Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. Charizard"
                    value={manualCard.name}
                    onChange={(e) => setManualCard((p) => ({ ...p, name: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Game <span className="text-red-500">*</span></label>
                  <select
                    value={manualCard.game}
                    onChange={(e) => setManualCard((p) => ({ ...p, game: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  >
                    {GAMES.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
                      Set Name
                      {setImporting && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-brand-500 font-normal">
                          <Loader2 className="h-3 w-3 animate-spin" /> Loading cards…
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      list="set-name-options"
                      placeholder="e.g. Base Set"
                      value={manualCard.set_name}
                      onChange={(e) => handleSetNameChange(e.target.value)}
                      className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                    <datalist id="set-name-options">
                      {(availableSets as string[]).map((s) => (
                        <option key={s} value={s} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Card Number</label>
                    <input
                      type="text"
                      placeholder="e.g. 4/102"
                      value={manualCard.card_number}
                      onChange={(e) => setManualCard((p) => ({ ...p, card_number: e.target.value }))}
                      className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Rarity</label>
                    <input
                      type="text"
                      placeholder="e.g. Holo Rare"
                      value={manualCard.rarity}
                      onChange={(e) => setManualCard((p) => ({ ...p, rarity: e.target.value }))}
                      className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Card Type</label>
                    <input
                      type="text"
                      placeholder="e.g. Fire"
                      value={manualCard.card_type}
                      onChange={(e) => setManualCard((p) => ({ ...p, card_type: e.target.value }))}
                      className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Photos */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                Card Photos <span className="text-gray-500 dark:text-zinc-500 font-normal">(optional, up to 5)</span>
              </p>
              {imagePreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {imagePreviews.map((src, i) => (
                    <div key={i} className="relative h-20 w-16 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-800 group">
                      <img src={src} alt={`preview ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(i)}
                        className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {imagePreviews.length < 5 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="h-20 w-16 rounded-lg border-2 border-dashed border-gray-200 dark:border-zinc-800 flex items-center justify-center hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:bg-brand-900/20 transition-colors"
                    >
                      <ImagePlus className="h-5 w-5 text-gray-500 dark:text-zinc-500" />
                    </button>
                  )}
                </div>
              )}
              {imagePreviews.length === 0 && (
                <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-200 dark:border-zinc-800 rounded-xl cursor-pointer hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:bg-brand-900/20 transition-colors">
                  <Upload className="h-6 w-6 text-gray-500 dark:text-zinc-500 mb-1" />
                  <span className="text-sm text-gray-500 dark:text-zinc-500">Click to upload photos</span>
                  <span className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">JPG, PNG, WebP · up to 5MB each</span>
                  <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageSelect} />
                </label>
              )}
              {imagePreviews.length > 0 && (
                <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageSelect} />
              )}
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
              className="w-full py-3 bg-brand-600 text-white rounded-xl font-semibold hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Continue →
            </button>
          </div>
        )}

        {/* ── STEP 2: Collection Details ───────────────────────────────────── */}
        {step === 2 && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6">
            <h2 className="text-xl font-bold mb-5">Collection Details</h2>

            {/* Condition */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Condition <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-3 gap-2">
                {CONDITIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCondition(c)}
                    className={`py-2 px-3 rounded-xl text-sm font-medium border transition-colors ${
                      condition === c
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white dark:bg-zinc-900 text-gray-700 dark:text-zinc-300 border-gray-200 dark:border-zinc-800 hover:border-brand-400'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Quantity</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="h-9 w-9 rounded-lg border border-gray-200 dark:border-zinc-800 flex items-center justify-center hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 disabled:opacity-40"
                >
                  –
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(9999, Number(e.target.value))))}
                  min={1} max={9999}
                  className="w-20 text-center border border-gray-200 dark:border-zinc-800 rounded-lg py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(9999, q + 1))}
                  className="h-9 w-9 rounded-lg border border-gray-200 dark:border-zinc-800 flex items-center justify-center hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900"
                >
                  +
                </button>
                <span className="text-sm text-gray-500 dark:text-zinc-500">copies</span>
              </div>
            </div>

            {/* Acquisition price */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                What did you pay? <span className="font-normal text-gray-500 dark:text-zinc-500">(optional)</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 text-sm">$</span>
                <input
                  type="number"
                  value={acquisitionPrice}
                  onChange={(e) => setAcquisitionPrice(e.target.value)}
                  placeholder="0.00"
                  step="0.01" min="0"
                  className="w-full pl-7 pr-4 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                Notes <span className="font-normal text-gray-500 dark:text-zinc-500">(optional)</span>
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Foil, Signed, 1st Edition…"
                maxLength={200}
                className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </div>

            {/* Make me an offer toggle */}
            <label className="flex items-center gap-3 cursor-pointer p-4 rounded-xl border border-gray-200 dark:border-zinc-800 hover:border-brand-200 hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:bg-brand-900/20 transition-colors mb-6">
              <input
                type="checkbox"
                checked={isForTrade}
                onChange={(e) => setIsForTrade(e.target.checked)}
                className="accent-brand-600 h-4 w-4 flex-shrink-0"
              />
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-zinc-200">Open to offers / trades</p>
                <p className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">Shows a "Make me an offer" banner on your collection for others to see</p>
              </div>
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 py-3 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm font-medium hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 py-3 bg-brand-600 text-white rounded-xl font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Adding…' : 'Add to Collection'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
